<template>
  <div>
    <Fieldset :title="props.title">
      <FormRow>
        <FormFiles
          v-model="product.cardImage"
          name="cardImage"
          :ref="addFormInputRef"
          :label="t('products.form.image-card')"
          :tooltip="t(`products.form.image-card-tooltip`)"
          :possiblesTypes="['image/png', 'image/jpg', 'image/jpeg', 'image/webp']"
          :default-selected-files="product.savedCardImage != null ? [product.savedCardImage] : []"
          can-delete
          @on-default-selected-files-change="onSavedImageChange($event, 'card')"
          @change="onImageChange($event, 'card')"
          @validated="handleValidation"
        />
        <FormFiles
          v-model="product.cardImageEn"
          name="cardImageEn"
          :ref="addFormInputRef"
          :label="t('products.form.image-card-en')"
          :tooltip="t(`products.form.image-card-tooltip`)"
          :possiblesTypes="['image/png', 'image/jpg', 'image/jpeg', 'image/webp']"
          :default-selected-files="product.savedCardImageEn != null ? [product.savedCardImageEn] : []"
          can-delete
          @on-default-selected-files-change="onSavedImageChange($event, 'cardEn')"
          @change="onImageChange($event, 'cardEn')"
          @validated="handleValidation"
        />
      </FormRow>

      <slot name="image"></slot>

      <slot name="after-images"></slot>

      <FormRow>
        <FormInput
          v-model="product.nameFr"
          type="text"
          name="name-fr"
          :ref="addFormInputRef"
          :label="t(props.nameFrLabel ?? 'products.form.name-fr')"
          @validated="handleValidation" />
        <FormInput
          v-model="product.nameEn"
          type="text"
          name="name-en"
          :ref="addFormInputRef"
          :label="t(props.nameEnLabel ?? 'products.form.name-en')"
          @validated="handleValidation" />
      </FormRow>

      <FormTextEditor
        v-model="product.descriptionFr"
        :ref="addFormInputRef"
        name="description-fr"
        :label="t(props.descriptionFrLabel ?? 'products.form.description-fr')"
        @validated="handleValidation"
      />
      <FormTextEditor
        v-model="product.descriptionEn"
        :ref="addFormInputRef"
        name="description-en"
        :label="t(props.descriptionEnLabel ?? 'products.form.description-en')"
        @validated="handleValidation"
      />

      <slot name="before-prices"></slot>

    </Fieldset>

    <Fieldset :title="t('products.form.price')">
      <FormRow>
        <FormInput
          type="number"
          name="member-price"
          v-model.number="product.memberPrice"
          :ref="addFormInputRef"
          :label="t('products.form.memberPrice')"
          :rules="[required, min(0)]"
          :tooltip="t('products.form.no-taxes')"
          @validated="handleValidation"
        />
        <FormInput
          v-if="!product.membersOnly"
          type="number"
          name="non-member-price"
          v-model.number="product.nonMemberPrice"
          :ref="addFormInputRef"
          :label="t('products.form.nonMemberPrice')"
          :rules="product.membersOnly ? [] : [required, min(0)]"
          :tooltip="t('products.form.no-taxes')"
          @validated="handleValidation"
        />
      </FormRow>

      <FormCheckbox
        name="membersOnly"
        v-model="product.membersOnly"
        :ref="addFormInputRef"
        :label="t('products.form.membersOnly')"
        @validated="handleValidation"
        @update:modelValue="newValue => {if (newValue) product.nonMemberPrice = undefined }"
      />
    </Fieldset>

    <Fieldset :title="t('products.form.accountingSoftwareConfiguration')">
      <FormTooltip>
        <p v-html="t(`products.form.accountingSoftwareConfiguration.tooltip`)"></p>
      </FormTooltip>
      <FormRow>
        <div>
          <FormSearchableSelect
            name="account-code-number"
            v-model="product.accountCode"
            :ref="addFormInputRef"
            :label="t('products.form.accountingSoftwareConfiguration.accountCode')"
            :options="accountCodeOptions"
            :rules="[]"
            @validated="handleValidation"
          />
          <button type="button" class="btn btn--add-code" @click="showAddAccountCodePopup = true">{{ t('global.add') }}</button>
        </div>

        <div>
          <FormSearchableSelect
            name="item-code"
            v-model="product.itemCode"
            :ref="addFormInputRef"
            :label="t('products.form.accountingSoftwareConfiguration.itemCode')"
            :options="itemCodeOptions"
            :rules="[]"
            @validated="handleValidation"
          />
          <button type="button" class="btn btn--add-code" @click="showAddItemCodePopup = true">{{ t('global.add') }}</button>
        </div>
      </FormRow>

      <AddAccountCodePopup
        v-if="showAddAccountCodePopup"
        @close-popup="showAddAccountCodePopup = false"
        @code-created="onAccountCodeCreated"
      />
      <AddItemCodePopup
        v-if="showAddItemCodePopup"
        @close-popup="showAddItemCodePopup = false"
        @code-created="onItemCodeCreated"
      />
    </Fieldset>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from "vue";
import { Status } from "@/validation";
import { required, min } from "@/validation/rules";
import { useI18n } from "vue3-i18n";
import FormInput from "@/components/forms/FormInput.vue";
import FormSearchableSelect from "@/components/forms/FormSearchableSelect.vue";
import Fieldset from "@/components/forms/Fieldset.vue";
import FormCheckbox from "@/components/forms/FormCheckbox.vue";
import FormRow from "@/components/forms/FormRow.vue";
import FormFiles from "@/components/forms/FormFiles.vue";
import {Product} from "@/types/entities/product";
import FormTextEditor from "@/components/forms/FormTextEditor.vue";
import FormTooltip from "@/components/forms/FormTooltip.vue";
import AddItemCodePopup from "@/components/products/AddItemCodePopup.vue";
import AddAccountCodePopup from "@/components/products/AddAccountCodePopup.vue";
import {FormOption} from "@/types/formOption";
import {useAccountingSoftwareCodeService} from "@/inversify.config";

const { t } = useI18n();
const accountingSoftwareCodeService = useAccountingSoftwareCodeService();

const itemCodeOptions = ref<FormOption[]>([]);
const accountCodeOptions = ref<FormOption[]>([]);
const showAddItemCodePopup = ref(false);
const showAddAccountCodePopup = ref(false);

onMounted(async () => {
  const { itemCodes, accountCodes } = await accountingSoftwareCodeService.getAccountingSoftwareCodes(props.type);

  itemCodeOptions.value = itemCodes.map(code => ({
    name: code.value,
    label: code.value
  }));

  accountCodeOptions.value = accountCodes.map(code => ({
    name: code.value,
    label: `${code.value} - ${code.name}`
  }));
});

// eslint-disable-next-line no-undef
const props = defineProps<{
    product: Product,
    title: string
    type: string
    nameFrLabel?: string
    nameEnLabel?: string
    descriptionFrLabel?: string
    descriptionEnLabel?: string
}>()

// eslint-disable-next-line
const emit = defineEmits<{
    (event: "handleValidation", name: string, validationStatus: Status): void
    (event: "registerFormInputRef", ref: typeof FormInput): void
}>()

const product = ref<Product>(props.product)

function addFormInputRef(ref: typeof FormInput) {
    emit("registerFormInputRef", ref);
}

async function onImageChange(event: any, type: string) {
  const file = event?.target?.files[0];
  if (file && type === "card") product.value.cardImage = file as File
  if (file && type === "cardEn") product.value.cardImageEn = file as File
}

async function onSavedImageChange(url: string, type: string) {
  if (type === "card") product.value.savedCardImage = url
  if (type === "cardEn") product.value.savedCardImageEn = url
}

async function handleValidation(name: string, validationStatus: Status) {
    emit("handleValidation", name, validationStatus);
}

function onItemCodeCreated(code: { value: string }) {
  const option = { name: code.value, label: code.value };
  itemCodeOptions.value.push(option);
  product.value.itemCode = code.value;
}

function onAccountCodeCreated(code: { value: string, name: string }) {
  const option = { name: code.value, label: `${code.value} - ${code.name}` };
  accountCodeOptions.value.push(option);
  product.value.accountCode = code.value;
}
</script>
